import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { supabase } from '../lib/supabase'
import type { AuthError, AuthChangeEvent, Session } from '@supabase/supabase-js'
import type { 
  AuthContextType, 
  AuthState, 
  SignUpData, 
  SignInData, 
  PasswordChangeData,
  PasswordUpdateData,
  ProfileUpdateData 
} from '../types/auth'

// Create the authentication context
const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Authentication provider component
interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
    error: null,
    successMessage: null
  })

  // Initialize authentication state
  useEffect(() => {
    // Get initial session
    const initializeAuth = async () => {
      try {
        console.log('[AuthContext] Initializing auth...')
        const { data: { session }, error } = await supabase.auth.getSession()
        console.log('[AuthContext] Session retrieved:', session?.user?.email || 'No session')

        if (error) {
          console.error('[AuthContext] Error getting session:', error)
          setAuthState(prev => ({
            ...prev,
            loading: false,
            error: error.message
          }))
          return
        }

        console.log('[AuthContext] Setting auth state - user:', session?.user?.email || 'null')
        setAuthState({
          user: session?.user || null,
          session: session,
          loading: false,
          error: null,
          successMessage: null
        })
      } catch (error) {
        console.error('Error initializing auth:', error)
        setAuthState(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to initialize authentication'
        }))
      }
    }

    const setupAuth = async () => {
      await initializeAuth()

      // Listen for auth state changes
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (_event: AuthChangeEvent, session: Session | null) => {
          setAuthState({
            user: session?.user || null,
            session: session,
            loading: false,
            error: null,
            successMessage: null
          })
        }
      )

      // Cleanup subscription on unmount
      return () => {
        subscription.unsubscribe()
      }
    }

    setupAuth()
  }, [])

  // Sign up function
  const signUp = async (data: SignUpData): Promise<{ error?: AuthError; success?: string }> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null, successMessage: null }))
      
      // Validate password confirmation
      if (data.password !== data.confirmPassword) {
        const error = new Error('Passwords do not match') as AuthError
        setAuthState(prev => ({ ...prev, loading: false, error: error.message }))
        return { error }
      }

      console.log('🔧 Attempting signup with:', { email: data.email, passwordLength: data.password.length })

      const { data: authData, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
      })

      console.log('🔧 Signup response:', { authData, error })
      console.log('🔧 Full authData details:', JSON.stringify(authData, null, 2))
      console.log('🔧 User object:', authData?.user)
      console.log('🔧 Session object:', authData?.session)
      console.log('🔧 User ID:', authData?.user?.id)
      console.log('🔧 User email:', authData?.user?.email)
      console.log('🔧 User confirmed at:', authData?.user?.email_confirmed_at)
      console.log('🔧 User created at:', authData?.user?.created_at)

      if (error) {
        console.error('❌ Signup error:', error)
        setAuthState(prev => ({ ...prev, loading: false, error: error.message }))
        return { error }
      }

      // If user needs to confirm email, show success message
      if (authData.user && !authData.session) {
        const successMsg = 'Please check your email to confirm your account.'
        setAuthState(prev => ({ 
          ...prev, 
          loading: false, 
          error: null,
          successMessage: successMsg
        }))
        console.log('✅ Signup successful - email confirmation required')
        return { success: successMsg }
      }

      // If user is immediately signed in (email confirmation disabled)
      console.log('✅ Signup successful - user immediately signed in')
      return {}
    } catch (error) {
      console.error('❌ Signup exception:', error)
      const authError = error as AuthError
      setAuthState(prev => ({ ...prev, loading: false, error: authError.message }))
      return { error: authError }
    }
  }

  // Sign in function
  const signIn = async (data: SignInData): Promise<{ error?: AuthError }> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }))
      
      const { error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      })

      if (error) {
        setAuthState(prev => ({ ...prev, loading: false, error: error.message }))
        return { error }
      }

      // Success case handled by auth state change listener
      return {}
    } catch (error) {
      const authError = error as AuthError
      setAuthState(prev => ({ ...prev, loading: false, error: authError.message }))
      return { error: authError }
    }
  }

  // Sign out function
  const signOut = async (): Promise<{ error?: AuthError }> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }))

      // Explicitly specify scope to avoid Vite build config issues
      // The 'global: globalThis' transform in vite.config was breaking this
      const { error } = await supabase.auth.signOut({ scope: 'local' })

      if (error) {
        setAuthState(prev => ({ ...prev, loading: false, error: error.message }))
        return { error }
      }

      // Success case handled by auth state change listener
      return {}
    } catch (error) {
      const authError = error as AuthError
      setAuthState(prev => ({ ...prev, loading: false, error: authError.message }))
      return { error: authError }
    }
  }

  // Change password function
  const changePassword = async (data: PasswordChangeData): Promise<{ error?: AuthError }> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }))
      
      // Validate password confirmation
      if (data.newPassword !== data.confirmNewPassword) {
        const error = new Error('New passwords do not match') as AuthError
        setAuthState(prev => ({ ...prev, loading: false, error: error.message }))
        return { error }
      }

      const { error } = await supabase.auth.updateUser({
        password: data.newPassword
      })

      if (error) {
        setAuthState(prev => ({ ...prev, loading: false, error: error.message }))
        return { error }
      }

      setAuthState(prev => ({ ...prev, loading: false }))
      return {}
    } catch (error) {
      const authError = error as AuthError
      setAuthState(prev => ({ ...prev, loading: false, error: authError.message }))
      return { error: authError }
    }
  }

  // Update password function (for password reset flow)
  const updatePassword = async (data: PasswordUpdateData): Promise<{ error?: AuthError }> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }))

      const { error } = await supabase.auth.updateUser({
        password: data.password
      })

      if (error) {
        setAuthState(prev => ({ ...prev, loading: false, error: error.message }))
        return { error }
      }

      setAuthState(prev => ({ ...prev, loading: false }))
      return {}
    } catch (error) {
      const authError = error as AuthError
      setAuthState(prev => ({ ...prev, loading: false, error: authError.message }))
      return { error: authError }
    }
  }

  // Update profile function
  const updateProfile = async (data: ProfileUpdateData): Promise<{ error?: AuthError }> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }))

      const { error } = await supabase.auth.updateUser({
        email: data.email,
        data: { 
          full_name: data.full_name 
        }
      })

      if (error) {
        setAuthState(prev => ({ ...prev, loading: false, error: error.message }))
        return { error }
      }

      setAuthState(prev => ({ ...prev, loading: false }))
      return {}
    } catch (error) {
      const authError = error as AuthError
      setAuthState(prev => ({ ...prev, loading: false, error: authError.message }))
      return { error: authError }
    }
  }

  // Clear error function
  const clearError = () => {
    setAuthState(prev => ({ ...prev, error: null }))
  }

  // Clear success message function
  const clearSuccess = () => {
    setAuthState(prev => ({ ...prev, successMessage: null }))
  }

  // Context value
  const value: AuthContextType = {
    user: authState.user,
    session: authState.session,
    loading: authState.loading,
    error: authState.error,
    successMessage: authState.successMessage,
    signUp,
    signIn,
    signOut,
    changePassword,
    updatePassword,
    updateProfile,
    clearError,
    clearSuccess
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}